import { useMemo, useState } from "react";
import { CalendarClock, CheckCircle2, MessageCircle, Phone, User, ChevronDown } from "lucide-react";
import { SITE, waHref } from "@/data/site";

const SERVICE_OPTIONS = [
  { value: "Cilt Bakımı (HydraFacial, Kolajen, Algae…)", label: "Cilt Bakımı (HydraFacial, Kolajen, Algae…)" },
  { value: "Epilasyon (Lazer / İğneli)", label: "Epilasyon (Lazer / İğneli)" },
  { value: "Kalıcı Makyaj (Kaş, Dipliner, Dudak, İpek Kirpik)", label: "Kalıcı Makyaj (Kaş, Dipliner, Dudak, İpek Kirpik)" },
  { value: "Podoloji / Medikal Ayak Bakımı", label: "Podoloji / Medikal Ayak Bakımı" },
  { value: "Bölgesel Sıkılaşma (G5 / EMS)", label: "Bölgesel Sıkılaşma (G5 / EMS)" },
  { value: "Diğer / Danışmanlık", label: "Diğer / Danışmanlık" },
];

export default function QuickAppointment({ defaultService = "", compact = false, title, subtitle }) {
  const [form, setForm] = useState({
    name: "",
    service: defaultService || "",
    preferred_date: "",
    note: "",
  });
  const [sent, setSent] = useState(false);
  const [savedName, setSavedName] = useState("");

  const nameValid = form.name.trim().length >= 2;
  const serviceValid = !!form.service;
  const canSubmit = nameValid && serviceValid;

  const waMessage = useMemo(() => {
    const parts = [
      "Merhaba MediEste,",
      "Sitedeki hızlı randevu formundan yazıyorum.",
      "",
      `👤 Ad Soyad: ${form.name || "-"}`,
      `💆 Hizmet: ${form.service || "Danışmanlık"}`,
    ];
    if (form.preferred_date) parts.push(`🗓️ Tercih ettiğim zaman: ${form.preferred_date}`);
    if (form.note) parts.push(`📝 Not: ${form.note}`);
    parts.push("");
    parts.push("Bilgi verirseniz sevinirim, teşekkürler.");
    return parts.join("\n");
  }, [form]);

  const setField = (k) => (e) => {
    const v = e.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  function handleSubmit(e) {
    e?.preventDefault();
    if (!canSubmit) return;
    setSavedName(form.name.split(" ")[0]);
    try {
      window.open(waHref(waMessage), "_blank", "noopener,noreferrer");
    } catch {
      window.location.href = waHref(waMessage);
    }
    setSent(true);
  }

  if (sent) {
    return (
      <div className="rounded-2xl bg-white border border-[#DDEBD1] p-6 md:p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-[#6CA320]/10 text-[#4F8B2A] flex items-center justify-center shrink-0">
            <CheckCircle2 size={26} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#4F8B2A] font-bold mb-2">WhatsApp&apos;a Yönlendirildiniz</p>
            <h3 className="font-display text-2xl md:text-3xl text-[#0B1B33] leading-tight mb-3">Teşekkürler {savedName}! 🌿</h3>
            <p className="text-[#4B5A6E] text-sm md:text-base leading-relaxed mb-5">
              Bilgileriniz otomatik olarak bir WhatsApp mesajına dönüştürüldü. Yeni sekmede mesajı bize <b>gönder</b>&apos;e basmanız yeterli. Açılmadıysa aşağıdan tekrar deneyebilirsiniz.
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <a
                href={waHref(waMessage)}
                target="_blank"
                rel="noreferrer"
                data-testid="qappt-success-wa"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#10B981] text-white text-[11px] tracking-[0.15em] uppercase font-bold hover:brightness-110 transition"
              >
                <MessageCircle size={14} /> WhatsApp&apos;ı Aç
              </a>
              <a
                href={`tel:${SITE.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border-2 border-[#0E2B4F] text-[#0E2B4F] text-[11px] tracking-[0.15em] uppercase font-bold hover:bg-[#0E2B4F] hover:text-white transition"
              >
                <Phone size={14} /> {SITE.phone}
              </a>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setForm({ name: "", service: defaultService || "", preferred_date: "", note: "" });
                }}
                className="text-xs text-[#4B5A6E] hover:text-[#0B1B33] tracking-wide underline underline-offset-4 self-center"
              >
                Yeni bir talep gönder
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="quick-appointment-form"
      className={`rounded-2xl bg-white border border-[#E5EAF0] shadow-sm ${compact ? "p-5 md:p-6" : "p-6 md:p-8"}`}
    >
      {(title || subtitle) && (
        <div className="mb-5">
          {title && (
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock size={18} className="text-[#4F8B2A]" />
              <p className="chip-green">Hızlı Randevu</p>
            </div>
          )}
          {title && (
            <h3 className={`font-display ${compact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"} text-[#0B1B33] leading-tight tracking-tight`}>{title}</h3>
          )}
          {subtitle && (
            <p className="text-[#4B5A6E] text-sm md:text-base leading-relaxed mt-2">{subtitle}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:gap-4">
        <div>
          <label className="qappt-label">
            <User size={14} /> Ad Soyad
          </label>
          <input
            type="text"
            value={form.name}
            onChange={setField("name")}
            placeholder="Adınız ve soyadınız"
            className="qappt-input"
            required
            minLength={2}
            maxLength={80}
            data-testid="qappt-name"
          />
        </div>

        <div>
          <label className="qappt-label">
            Hizmet
          </label>
          <div className="relative">
            <select
              value={form.service}
              onChange={setField("service")}
              className="qappt-input appearance-none pr-10"
              required
              data-testid="qappt-service"
            >
              <option value="" disabled>Bir hizmet seçin</option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7C8CA0] pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="qappt-label">
            Tercih ettiğiniz gün / saat <span className="text-[#7C8CA0] font-normal">(opsiyonel)</span>
          </label>
          <input
            type="text"
            value={form.preferred_date}
            onChange={setField("preferred_date")}
            placeholder="Örn: Cumartesi öğleden sonra"
            className="qappt-input"
            maxLength={40}
            data-testid="qappt-date"
          />
        </div>

        <div>
          <label className="qappt-label">
            Notunuz <span className="text-[#7C8CA0] font-normal">(opsiyonel)</span>
          </label>
          <textarea
            value={form.note}
            onChange={setField("note")}
            placeholder="Bize bahsetmek istediğiniz bir şey var mı?"
            rows={compact ? 2 : 3}
            className="qappt-input resize-none"
            maxLength={500}
            data-testid="qappt-note"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={!canSubmit}
          className={`btn-green btn-green--tall flex-1 ${!canSubmit ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid="qappt-submit"
        >
          <MessageCircle size={16} /> Randevu Al
        </button>
        <a
          href={`tel:${SITE.phoneRaw}`}
          className="btn-outline btn-green--tall"
          data-testid="qappt-call"
        >
          <Phone size={14} /> Ara
        </a>
      </div>

      <p className="mt-3 text-[11px] text-[#7C8CA0] leading-relaxed">
        Butona bastığınızda bilgileriniz otomatik olarak WhatsApp mesajına eklenir — sadece <b>Gönder</b>&apos;e basmanız yeterli.
      </p>
    </form>
  );
}
